import { useEffect, useState } from 'react';
import { api } from '../../core/apiClient.ts';
import type { LaunchTemplate, LaunchTemplateListItem } from './types.ts';

export type AdAccount = { id: string; name: string | null; currency: string | null; social_account_id: string | null };
export type Page = { id: string; name: string | null; social_account_id: string | null };
export type Pixel = { id: string; name: string | null };
export type Post = { id: string; message: string | null };
export type Payer = { beneficiaryName: string | null; payerName: string | null };
export type WizardTarget = {
  local_id: string;        // crypto.randomUUID() — unique per row, used as React key and for addressing
  ad_account_id: string;   // kept — no longer unique per row, account may have multiple rows
  page_id: string;
  pixel_id: string | null;
  post_id: string;
  link_url: string;
  budget_override: number | null;
  beneficiary_name: string;
  payer_name: string;
  url_tags: string;
  custom_name: string;
  scheduled_at: string | null;
};

function makeEmptyTarget(accountId: string, budget: number | null, scheduledAt: string | null): WizardTarget {
  return {
    local_id: crypto.randomUUID(),
    ad_account_id: accountId,
    page_id: '',
    pixel_id: null,
    post_id: '',
    link_url: '',
    budget_override: budget,
    beneficiary_name: '',
    payer_name: '',
    url_tags: '',
    custom_name: '',
    scheduled_at: scheduledAt,
  };
}

const STORAGE_PAUSE_KEY = 'launch-last-pause';
const STORAGE_SCHEDULED_KEY = 'launch-last-scheduled';
const STORAGE_TZ_KEY = 'launch-tz-offset';

function readStoredTz(): number {
  const v = localStorage.getItem(STORAGE_TZ_KEY);
  if (v !== null && !isNaN(Number(v))) return Number(v);
  return 3; // Default to +3
}

function readStoredPause(): number {
  try {
    const val = localStorage.getItem(STORAGE_PAUSE_KEY);
    if (val !== null) {
      const n = Number(val);
      if (!isNaN(n) && n >= 0) return n;
    }
  } catch { /* ignore */ }
  return 30;
}

function readStoredScheduled(): string | null {
  try {
    return localStorage.getItem(STORAGE_SCHEDULED_KEY) ?? null;
  } catch { /* ignore */ }
  return null;
}

export function useLaunchWizard() {
  const [templates, setTemplates] = useState<LaunchTemplateListItem[]>([]);
  const [adAccounts, setAdAccounts] = useState<AdAccount[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [pixels, setPixels] = useState<Record<string, Pixel[]>>({});
  const [posts, setPosts] = useState<Record<string, Post[]>>({});
  const [payers, setPayers] = useState<Record<string, Payer[]>>({});
  const [allPayers, setAllPayers] = useState<Payer[]>([]);
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [templateBudget, setTemplateBudget] = useState<number | null>(null);
  const [scheduledAt, setScheduledAtState] = useState<string | null>(readStoredScheduled);
  const [pauseSeconds, setPauseSecondsState] = useState<number>(readStoredPause);
  const [tzOffsetHours, setTzOffsetHoursState] = useState<number>(readStoredTz);
  // Key: ad_account_id, Value: ordered array of campaign rows for that account
  const [targets, setTargets] = useState<Record<string, WizardTarget[]>>({});
  // selectedOrder tracks the order in which accounts were selected
  const [selectedOrder, setSelectedOrder] = useState<string[]>([]);
  const [adsetsCount, setAdsetsCount] = useState(1);
  const [adsPerAdset, setAdsPerAdset] = useState(1);

  useEffect(() => {
    Promise.all([
      api.get<LaunchTemplateListItem[]>('/api/launch-templates'),
      api.get<AdAccount[]>('/api/launcher/ad-accounts'),
      api.get<Page[]>('/api/launcher/pages'),
      api.get<Payer[]>('/api/launcher/payers'),
    ]).then(([t, a, p, globalPayers]) => { setTemplates(t); setAdAccounts(a); setPages(p); setAllPayers(globalPayers); }).catch(console.error);
  }, []);

  const setPauseSeconds = (n: number) => {
    setPauseSecondsState(n);
    try { localStorage.setItem(STORAGE_PAUSE_KEY, String(n)); } catch { /* ignore */ }
  };

  const setTzOffsetHours = (n: number) => {
    setTzOffsetHoursState(n);
    try { localStorage.setItem(STORAGE_TZ_KEY, String(n)); } catch { /* ignore */ }
  };

  const setScheduledAt = (val: string | null) => {
    setScheduledAtState(val);
    try {
      if (val) localStorage.setItem(STORAGE_SCHEDULED_KEY, val);
      else localStorage.removeItem(STORAGE_SCHEDULED_KEY);
    } catch { /* ignore */ }
  };

  const loadPixels = async (accountId: string) => {
    try {
      const data = await api.get<Pixel[]>(`/api/launcher/pixels/${accountId}`);
      setPixels((old) => ({ ...old, [accountId]: data }));
      if (data.length > 0) {
        // Auto-fill pixel_id on all rows for this account that don't have one yet
        setTargets((old) => {
          const rows = old[accountId];
          if (!rows) return old;
          const updated = rows.map((r) => r.pixel_id ? r : { ...r, pixel_id: data[0].id });
          return { ...old, [accountId]: updated };
        });
      }
    } catch (err) {
      console.error('Failed to load pixels:', err);
    }
  };
  const [syncingPosts, setSyncingPosts] = useState<Record<string, boolean>>({});
  const [postErrors, setPostErrors] = useState<Record<string, string>>({});
  const loadPosts = async (pageId: string, force = false) => {
    if (!posts[pageId] || force) {
      try {
        const data = await api.get<Post[]>(`/api/launcher/posts/${encodeURIComponent(pageId)}`);
        setPosts((old) => ({ ...old, [pageId]: data }));
        setPostErrors((old) => ({ ...old, [pageId]: '' }));
      } catch (error) {
        setPostErrors((old) => ({ ...old, [pageId]: error instanceof Error ? error.message : 'Could not load posts' }));
      }
    }
  };
  const syncPosts = async (pageId: string) => {
    if (syncingPosts[pageId]) return;
    setSyncingPosts((old) => ({ ...old, [pageId]: true }));
    setPostErrors((old) => ({ ...old, [pageId]: '' }));
    try {
      const result = await api.post<{ ok: boolean; error?: string }>(`/api/pages/${encodeURIComponent(pageId)}/sync-posts`);
      if (!result.ok) throw new Error(result.error || 'Could not sync posts');
      await loadPosts(pageId, true);
    } catch (error) {
      setPostErrors((old) => ({ ...old, [pageId]: error instanceof Error ? error.message : 'Could not sync posts' }));
    } finally {
      setSyncingPosts((old) => ({ ...old, [pageId]: false }));
    }
  };

  // When posts for a selected page arrive, use the newest one for rows where
  // the user has not explicitly chosen a post yet.
  useEffect(() => {
    setTargets((old) => {
      let changed = false;
      const next: Record<string, WizardTarget[]> = {};
      for (const [accountId, rows] of Object.entries(old)) {
        next[accountId] = rows.map((row) => {
          const latest = row.page_id ? posts[row.page_id]?.[0] : undefined;
          if (!row.post_id && latest) { changed = true; return { ...row, post_id: latest.id }; }
          return row;
        });
      }
      return changed ? next : old;
    });
  }, [posts]);
  const loadPayers = async (accountId: string) => {
    if (!payers[accountId]) {
      const data = await api.get<Payer[]>(`/api/launcher/payers/${accountId}`);
      setPayers((old) => ({ ...old, [accountId]: data }));
    }
  };

  const chooseTemplate = async (id: number | null) => {
    setTemplateId(id);
    setTemplateBudget(null);
    if (id) {
      const template = await api.get<LaunchTemplate>(`/api/launch-templates/${id}`);
      setTemplateBudget(template.config.campaign.budget_amount);
    }
  };

  const selectAccount = (accountId: string, selected: boolean, budget: number | null, globalScheduledAt?: string | null) => {
    setTargets((old) => {
      const next = { ...old };
      if (selected) {
        next[accountId] = [makeEmptyTarget(accountId, budget, globalScheduledAt ?? null)];
      } else {
        delete next[accountId];
      }
      return next;
    });
    setSelectedOrder((old) => {
      if (selected) return old.includes(accountId) ? old : [...old, accountId];
      return old.filter((id) => id !== accountId);
    });
    if (selected) {
      loadPixels(accountId);
    }
  };

  // Bulk-select multiple accounts at once (from Bulk Accs modal)
  const bulkSelectAccounts = (accountIds: string[], budget: number | null, globalScheduledAt?: string | null) => {
    setTargets((old) => {
      const next = { ...old };
      for (const accountId of accountIds) {
        if (!next[accountId]) {
          next[accountId] = [makeEmptyTarget(accountId, budget, globalScheduledAt ?? null)];
        }
      }
      return next;
    });
    setSelectedOrder((old) => {
      const result = [...old];
      for (const id of accountIds) {
        if (!result.includes(id)) result.push(id);
      }
      return result;
    });
    accountIds.forEach((id) => loadPixels(id));
  };

  // Add a new campaign row for an account, cloning the last row's settings
  // (post_id and custom_name are reset since they're campaign-specific)
  const addCampaignRow = (accountId: string) => {
    setTargets((old) => {
      const rows = old[accountId] ?? [];
      const last = rows[rows.length - 1];
      const cloned: WizardTarget = last
        ? { ...last, local_id: crypto.randomUUID(), post_id: '', custom_name: '' }
        : makeEmptyTarget(accountId, templateBudget, scheduledAt);
      return { ...old, [accountId]: [...rows, cloned] };
    });
  };

  const setCampaignCountForAll = (count: number) => {
    const targetCount = Math.max(1, Math.floor(count) || 1);
    setTargets((old) => Object.fromEntries(Object.entries(old).map(([accountId, rows]) => {
      const next = rows.slice(0, targetCount);
      while (next.length < targetCount) {
        const last = next[next.length - 1];
        next.push(last ? { ...last, local_id: crypto.randomUUID(), post_id: '', custom_name: '' } : makeEmptyTarget(accountId, templateBudget, scheduledAt));
      }
      return [accountId, next];
    })));
  };

  // Remove a specific campaign row by local_id.
  // If it's the last row for this account, also removes the account from selectedOrder.
  const removeCampaignRow = (accountId: string, localId: string) => {
    setTargets((old) => {
      const rows = (old[accountId] ?? []).filter((r) => r.local_id !== localId);
      if (rows.length === 0) {
        const next = { ...old };
        delete next[accountId];
        setSelectedOrder((o) => o.filter((id) => id !== accountId));
        return next;
      }
      return { ...old, [accountId]: rows };
    });
  };

  const updateTarget = (accountId: string, localId: string, patch: Partial<WizardTarget>) =>
    setTargets((old) => ({
      ...old,
      [accountId]: (old[accountId] ?? []).map((r) =>
        r.local_id === localId ? { ...r, ...patch } : r
      ),
    }));

  // Apply a patch to ALL target rows across all accounts
  const bulkUpdateAllTargets = (patch: Partial<WizardTarget>) => {
    setTargets((old) => {
      const next: Record<string, WizardTarget[]> = {};
      for (const [accountId, rows] of Object.entries(old)) {
        next[accountId] = rows.map((r) => ({ ...r, ...patch }));
      }
      return next;
    });
  };

  const submitBatch = async () => {
    if (!templateId) throw new Error('Select a template');
    // Flatten Record<accountId, rows[]> → flat array with campaign_slot (1-based per account)
    const allRows = Object.entries(targets).flatMap(([accountId, rows]) =>
      rows.map((row, idx) => ({ ...row, ad_account_id: accountId, campaign_slot: idx + 1 }))
    );
    if (!allRows.length || allRows.some((r) => !r.page_id || !r.post_id || !r.pixel_id)) {
      throw new Error('Select a page, pixel, and post for every campaign');
    }
    return api.post<{ batch_run_id: string; launches_count: number }>('/api/launcher/batches', {
      template_id: templateId,
      scheduled_at: scheduledAt,
      pause_seconds: pauseSeconds,
      tz_offset: tzOffsetHours,
      adsets_count: adsetsCount,
      ads_per_adset: adsPerAdset,
      targets: allRows.map((t) => ({
        ...t,
        custom_name: t.custom_name.trim() || null,
        scheduled_at: t.scheduled_at || null,
      })),
    });
  };

  const reset = () => {
    setTemplateId(null);
    setTemplateBudget(null);
    // Note: pauseSeconds and scheduledAt are intentionally NOT reset — they persist across launches
    setTargets({});
    setSelectedOrder([]);
    setAdsetsCount(1);
    setAdsPerAdset(1);
  };

  return {
    templates, adAccounts, pages, pixels, posts, payers, allPayers,
    templateId, templateBudget, setTemplateId: chooseTemplate,
    scheduledAt, setScheduledAt, pauseSeconds, setPauseSeconds,
    tzOffsetHours, setTzOffsetHours,
    targets, selectedOrder,
    selectAccount, bulkSelectAccounts, updateTarget, bulkUpdateAllTargets,
    addCampaignRow, setCampaignCountForAll, removeCampaignRow,
    loadPixels, loadPosts, loadPayers, syncPosts, syncingPosts, postErrors,
    adsetsCount, setAdsetsCount, adsPerAdset, setAdsPerAdset,
    submitBatch, reset,
  };
}
